import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface TimeEntry {
  id: string
  employee_name: string
  project_name: string
  date: string
  hours: number
  task: string
  is_billable: boolean
  amount: number
  status: string
}

interface Timesheet {
  id: string
  employee_name: string
  week_start: string
  week_end: string
  total_hours: number
  billable_hours: number
  status: string
  total_amount: number
}

export default function TimeTrackingPage() {
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [entriesRes, timesheetsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/time-tracking/entries'),
      fetch(API_BASE + '/time-tracking/timesheets'),
      fetch(API_BASE + '/time-tracking/statistics')
    ])
    if (entriesRes.ok) setEntries((await entriesRes.json()).entries || [])
    if (timesheetsRes.ok) setTimesheets((await timesheetsRes.json()).timesheets || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800'
      case 'submitted': return 'bg-blue-100 text-blue-800'
      case 'draft': return 'bg-gray-100 text-gray-600'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Time Tracking</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Log Time</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Today</div>
              <div className="text-2xl font-bold">{statistics.hours_today}h</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">This Week</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.hours_this_week}h</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">This Month</div>
              <div className="text-2xl font-bold">{statistics.hours_this_month}h</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Billable MTD</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.billable_amount_mtd)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Utilization</div>
              <div className="text-2xl font-bold">{statistics.utilization_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Time Entries</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {entries.slice(0, 12).map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{entry.employee_name}</div>
                      <div className="text-xs text-gray-500">{entry.date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{entry.project_name}</div>
                      <div className="text-xs text-gray-500">{entry.task}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold">{entry.hours}h</div>
                      {entry.is_billable && <div className="text-xs text-green-600">{formatCurrency(entry.amount)}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(entry.status)}`}>{entry.status}</span>
                      {entry.is_billable && <span className="ml-2 text-xs text-green-600">Billable</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Timesheets</h3>
            <div className="space-y-3">
              {timesheets.slice(0, 10).map(ts => (
                <div key={ts.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{ts.employee_name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(ts.status)}`}>{ts.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{ts.week_start} to {ts.week_end}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-bold">{ts.total_hours}h total</span>
                    <span className="text-sm text-green-600">{ts.billable_hours}h billable</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
